import { useEffect, useState } from "react";
import { RotateCcw, Save } from "lucide-react";

import AdminLayout from "@/pages/admin/AdminLayout";
import { adminGetConfig, adminUpdateConfig } from "@/lib/api";
import { SEO_PAGES } from "@/lib/seoDefaults";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

// Recommended max lengths (Google typically truncates around these).
const TITLE_MAX = 60;
const DESC_MAX = 160;

export default function AdminSeo() {
  const { toast } = useToast();
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminGetConfig().then((cfg) => {
      const seo = cfg.seo || {};
      setRows(
        SEO_PAGES.map((p) => ({
          path: p.path,
          label: p.label,
          defTitle: p.title,
          defDescription: p.description,
          title: seo[p.path]?.title ?? p.title,
          description: seo[p.path]?.description ?? p.description,
        }))
      );
    });
  }, []);

  function update(path, field, value) {
    setRows((rs) => rs.map((r) => (r.path === path ? { ...r, [field]: value } : r)));
  }

  function reset(path) {
    setRows((rs) =>
      rs.map((r) =>
        r.path === path ? { ...r, title: r.defTitle, description: r.defDescription } : r
      )
    );
  }

  async function save() {
    setSaving(true);
    try {
      // Only persist real overrides; a field equal to the default is omitted so
      // the frontend keeps falling back to seoDefaults.js. PUT replaces the whole
      // `seo` object, so omitted routes are cleared back to their defaults.
      const seo = {};
      for (const r of rows) {
        const o = {};
        const t = r.title.trim();
        const d = r.description.trim();
        if (t && t !== r.defTitle) o.title = t;
        if (d && d !== r.defDescription) o.description = d;
        if (Object.keys(o).length) seo[r.path] = o;
      }
      await adminUpdateConfig({ seo });
      toast({ title: "SEO opgeslagen", description: "Herbouw de prerender-snapshots om de wijzigingen live te zien." });
    } catch (err) {
      toast({ title: "Opslaan mislukt", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  if (!rows) return <AdminLayout><p>Laden…</p></AdminLayout>;

  return (
    <AdminLayout>
      <div data-testid="admin-seo-page" className="space-y-8 max-w-4xl">
        <div>
          <h1 className="font-display text-3xl font-bold text-ink tracking-tight">SEO</h1>
          <p className="text-ink-soft mt-1 text-sm">
            Titel en meta-omschrijving per pagina. Leeg laten of op standaard zetten gebruikt de
            ingebouwde standaardtekst. Dynamische pagina's (blog, checks, bedrijfsprofielen) halen
            hun titel uit hun eigen inhoud en staan hier niet.
          </p>
        </div>

        {rows.map((r) => {
          const titleChanged = r.title.trim() !== r.defTitle;
          const descChanged = r.description.trim() !== r.defDescription;
          return (
            <section key={r.path} className="rounded-2xl bg-white border border-border p-6 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="font-display text-lg font-semibold text-ink">{r.label}</h2>
                  <code className="text-xs text-ink-soft">{r.path}</code>
                </div>
                {(titleChanged || descChanged) && (
                  <button
                    onClick={() => reset(r.path)}
                    className="inline-flex items-center gap-1.5 text-xs text-ink-soft hover:text-ink"
                    data-testid={`seo-reset-${r.path}`}
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> Standaard
                  </button>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label>Titel</Label>
                  <span className={`text-xs ${r.title.length > TITLE_MAX ? "text-red-500" : "text-ink-soft"}`}>
                    {r.title.length}/{TITLE_MAX}
                  </span>
                </div>
                <Input
                  value={r.title}
                  onChange={(e) => update(r.path, "title", e.target.value)}
                  data-testid={`seo-title-${r.path}`}
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label>Meta-omschrijving</Label>
                  <span className={`text-xs ${r.description.length > DESC_MAX ? "text-red-500" : "text-ink-soft"}`}>
                    {r.description.length}/{DESC_MAX}
                  </span>
                </div>
                <Textarea
                  rows={2}
                  value={r.description}
                  onChange={(e) => update(r.path, "description", e.target.value)}
                  data-testid={`seo-desc-${r.path}`}
                />
              </div>
            </section>
          );
        })}

        <div className="sticky bottom-4">
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 text-white px-5 py-3 font-semibold shadow-lg hover:bg-blue-700 disabled:opacity-60"
            data-testid="seo-save"
          >
            <Save className="h-4 w-4" /> {saving ? "Opslaan…" : "SEO opslaan"}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
